import {
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LoginService } from './service/login.service';
import validationform from '../../validation/validationform';
import * as CryptoJS from 'crypto-js';
import { NotifierService } from '../../services/Notifierservice';
import { Router } from '@angular/router';
import { VerifyService } from '../verify/service/verify.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MainService } from '../../views/layout/main/service/mainservice';
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoginComponent implements OnInit {
  showBtn = signal<Boolean>(false);
  spinner = signal<Boolean>(false);
  loginSubmitted = signal<Boolean>(false);
  showOtpField = signal<boolean>(false);
  otpInputValue: string = '';
  getEmailVerifyResponseData: any;
  permitedEmail: any;
  showVerifyForm = 'login';
  otpInput: any;
  otpVerifiedData: any;
  type: string = 'password';
  isText = false;
  eyeIcon = 'fa-eye-slash';
  passwordIcon = 'fa fa-lock';
  loginForm: FormGroup = new FormGroup({});

  captchaText: string = '';
  captchaInput: string = '';
  captchaVerified: boolean = false;
  captchaSubmitted: boolean = false;
  captchaReady = signal<boolean>(false);

  @Input() ReceiveSignUpVerify: any;
  @Output() formChange: EventEmitter<any> = new EventEmitter();
  private readonly notifier: NotifierService;
  readonly RFP_ID = 5;
  readonly RFP_NAME = 'RFP';

  checkRfpExists(response: any): boolean {
    return response?.apps?.some(
      (app: any) =>
        app?.id == this.RFP_ID && app?.name?.toUpperCase() == this.RFP_NAME,
    );
  }
  constructor(
    private readonly service: LoginService,
    private readonly mainService: MainService,
    private readonly verifyService: VerifyService,
    private readonly notifierService: NotifierService,
    private readonly router: Router,
  ) {
    this.notifier = notifierService;
  }

  ngOnInit() {
    this.loginForm = new FormGroup({
      email: new FormControl(null, [
        Validators.required,
        Validators.pattern(
          '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$',
        ),
      ]),
      pwd: new FormControl(null, [
        Validators.required,
        Validators.pattern(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9\s])\S{8,}$/,
        ),
      ]),
    });
    this.loginSubmitted.set(false);
    this.captchaReady.set(false);
    if (sessionStorage.getItem('isLoggedIn') && localStorage.getItem('user')) {
      const raw = localStorage.getItem('user')!;

      try {
        const [saltHex, ivHex, cipherStr] = raw.split('::'); // ✅ match new storage format
        const salt = CryptoJS.enc.Hex.parse(saltHex);
        const iv = CryptoJS.enc.Hex.parse(ivHex);
        const key = CryptoJS.PBKDF2(this.service.enCryptKey, salt, {
          keySize: 32,
          iterations: 128,
          hasher: CryptoJS.algo.SHA256,
        });
        const decrypted = CryptoJS.AES.decrypt(cipherStr, key, { iv });
        const userData = JSON.parse(decrypted.toString(CryptoJS.enc.Utf8));

        this.service.isAuthorizedUser = true;
        this.service.userDetails = userData; // ✅ parsed object, not raw string
      } catch (e) {
        localStorage.clear();
        sessionStorage.clear();
      }
    }
  }

  hideShowPassword(): void {
    this.isText = !this.isText;

    this.eyeIcon = this.isText ? 'fa-eye' : 'fa-eye-slash';
    this.type = this.isText ? 'text' : 'password';
  }

  validateUserEmail() {
    if (this.spinner()) return;
    const value = this.loginForm.value;
    const emailId = value.email;
    this.loginSubmitted.set(true);

    if (!this.loginForm.controls['email'].valid) {
      validationform.ValidateAllFormFields(this.loginForm);
      return;
    }

    if (this.captchaReady() && !this.captchaVerified) {
      this.captchaSubmitted = true;
      return;
    }

    this.spinner.set(true);

    this.service.getEmail(emailId).subscribe({
      next: (sub: any) => {
        this.loginSubmitted.set(false);
        this.getEmailVerifyResponseData = sub;

        // ── SAFE HANDLING: user not found returned as 200 ─────────────
        // Backend returns 200 with a message but no email/auth_mode
        // Move silently to OTP screen (OTP already sent by backend)
        if (!sub.email || !sub.auth_mode) {
          this.service.flowContext = {
            authMode: '',
            passwordReset: false,
            email: emailId, // use what the user typed — backend won't expose it
          };
          this.formChange.emit({
            form: 'verify',
            context: this.service.flowContext,
          });
          this.spinner.set(false);
          return;
        }
        // ──────────────────────────────────────────────────────────────

        this.permitedEmail = sub.email;
        localStorage.setItem('loginuser', sub.email || '');

        const authMode = sub.auth_mode;
        const signupVerified = sub.signup_verified;
        const passwordReset = sub.password_reset;

        if (signupVerified === true && passwordReset === false) {
          this.service.flowContext = {
            authMode,
            signupVerified,
            passwordReset,
            email: sub.email,
          };

          this.formChange.emit({
            form: 'verify',
            context: {
              ...this.service.flowContext,
              directPasswordSetup: true,
            },
          });

          this.spinner.set(false);
          return;
        }

        // ── OTP_ONLY + signup_verified TRUE ──────────────────────────
        // call twoFactorLogin first, then show verify form
        if (authMode === 'OTP_ONLY' && passwordReset) {
          this.service.twoFactorLogin(sub.email).subscribe({
            next: () => {
              this.service.flowContext = {
                authMode,
                passwordReset,
                email: sub.email,
              };
              this.formChange.emit({
                form: 'verify',
                context: this.service.flowContext,
              });
              this.notifier.notify(
                'default',
                'A verification code has been sent to your registered email and mobile.',
              );
              this.spinner.set(false);
            },
            error: (err: HttpErrorResponse) => {
              this.spinner.set(false);
              console.error(err);
            },
          });
          return;
        }

        // ── OTP_ONLY + signup_verified FALSE ─────────────────────────
        // OTP auto-sent by backend — show verify form directly
        if (authMode === 'OTP_ONLY' && !passwordReset) {
          this.service.flowContext = {
            authMode,
            passwordReset,
            email: sub.email,
          };
          this.formChange.emit({
            form: 'verify',
            context: this.service.flowContext,
          });
          this.spinner.set(false);
          return;
        }

        // ── PASSWORD_ONLY + signup_verified FALSE ─────────────────────
        // OTP auto-sent by backend — show verify form directly
        if (authMode === 'PASSWORD_ONLY' && !passwordReset) {
          this.service.flowContext = {
            authMode,
            passwordReset,
            email: sub.email,
          };
          this.formChange.emit({
            form: 'verify',
            context: this.service.flowContext,
          });
          this.spinner.set(false);
          return;
        }

        // ── PASSWORD_ONLY + signup_verified TRUE ──────────────────────
        // show password field + captcha
        if (authMode === 'PASSWORD_ONLY' && passwordReset) {
          this.showBtn.set(true);
          this.captchaReady.set(true);
          setTimeout(() => this.generateCaptcha());
          this.spinner.set(false);
          return;
        }

        // ── PASSWORD_OTP + signup_verified TRUE ───────────────────────
        // show password field only (OTP comes after password verified)
        if (authMode === 'PASSWORD_OTP' && passwordReset) {
          this.showBtn.set(true);
          this.showOtpField.set(false);
          this.captchaReady.set(false);
          this.spinner.set(false);
          return;
        }

        // ── PASSWORD_OTP + signup_verified FALSE ──────────────────────
        // OTP auto-sent by backend — show verify form directly
        if (authMode === 'PASSWORD_OTP' && !passwordReset) {
          this.service.flowContext = {
            authMode,
            passwordReset,
            email: sub.email,
          };
          this.formChange.emit({
            form: 'verify',
            context: this.service.flowContext,
          });
          this.spinner.set(false);
          return;
        }

        this.spinner.set(false);
      },
      error: (error: any) => {
        this.spinner.set(false);
        console.error(error);
      },
    });
  }

  makeRandom(lengthOfCode: number, possible: string) {
    let text = '';
    for (let i = 0; i < lengthOfCode; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

  submitOtp() {
    const authMode =
      this.getEmailVerifyResponseData?.auth_mode ??
      this.service.flowContext?.authMode;
    const passwordReset =
      this.getEmailVerifyResponseData?.signup_verified ??
      this.service.flowContext?.passwordReset;
    const email =
      (this.service.flowContext?.email || this.loginForm.value.email?.trim()) ??
      '';
    const otp = this.otpInput?.toString().trim();

    this.spinner.set(true);

    // ── OTP_ONLY + signup_verified TRUE ───────────────────────────────
    // verifyOtpFlow → token → dashboard
    if (authMode === 'OTP_ONLY' && passwordReset) {
      this.service.verifyOtpFlow(email, otp).subscribe({
        next: (response: any) => {
          sessionStorage.setItem('isLoggedIn', 'true');
          this.navigateToDashboard(email);
        },
        error: (err) => {
          this.spinner.set(false);
          console.error(err);
        },
      });
      return;
    }

    // ── OTP_ONLY + signup_verified FALSE ──────────────────────────────
    // signupVerifyOtpLogin → token → dashboard
    if (authMode === 'OTP_ONLY' && !passwordReset) {
      this.service.signupVerifyOtpLogin(email, otp).subscribe({
        next: (response: any) => {
          sessionStorage.setItem('isLoggedIn', 'true');
          this.navigateToDashboard(email);
        },
        error: (err) => {
          this.spinner.set(false);
          console.error(err);
        },
      });
      return;
    }

    // ── PASSWORD_ONLY + signup_verified FALSE ─────────────────────────
    // signupVerifyOtpLogin → token → dashboard
    if (authMode === 'PASSWORD_ONLY' && !passwordReset) {
      this.service.signupVerifyOtpLogin(email, otp).subscribe({
        next: (response: any) => {
          sessionStorage.setItem('isLoggedIn', 'true');
          this.navigateToDashboard(email);
        },
        error: (err) => {
          this.spinner.set(false);
          console.error(err);
        },
      });
      return;
    }

    // ── PASSWORD_OTP + signup_verified TRUE ───────────────────────────
    // verifyOtpFlow → token → dashboard
    if (authMode === 'PASSWORD_OTP' && passwordReset) {
      this.service.verifyOtpFlow(email, otp).subscribe({
        next: (response: any) => {
          sessionStorage.setItem('isLoggedIn', 'true');
          this.navigateToDashboard(email);
        },
        error: (err) => {
          this.spinner.set(false);
          console.error(err);
        },
      });
      return;
    }

    // ── PASSWORD_OTP + signup_verified FALSE ──────────────────────────
    // signupVerifyOtpLogin → token → dashboard
    if (authMode === 'PASSWORD_OTP' && !passwordReset) {
      this.service.signupVerifyOtpLogin(email, otp).subscribe({
        next: (response: any) => {
          sessionStorage.setItem('isLoggedIn', 'true');
          this.navigateToDashboard(email);
        },
        error: (err) => {
          this.spinner.set(false);
          console.error(err);
        },
      });
      return;
    }

    this.spinner.set(false);
  }

  async signIn() {
    if (this.spinner()) return; 

    if (!this.validateSignInForm()) {
      return;
    }

    const values = this.loginForm.value;
    const email = values.email.trim();
    const password = values.pwd.trim();

    const authMode = this.getEmailVerifyResponseData?.auth_mode;
    const passwordReset = this.getEmailVerifyResponseData?.signup_verified;

    if (this.handleOtpOnlyClick(authMode, passwordReset, email)) {
      return;
    }

    if (
      authMode === 'PASSWORD_OTP' &&
      !passwordReset &&
      !this.otpInputValue?.trim()
    ) {
      return;
    }

    this.spinner.set(true); 

    try {
      const encrypted = await this.encryptPassword(password);

      const formEncript: any = {
        email,
        encrypted_data: encrypted.encryptedData,
        salt: encrypted.salt,
        pass_phrase: encrypted.passPhrase,
      };

      if (authMode === 'PASSWORD_OTP' && !passwordReset) {
        formEncript.otp = this.otpInputValue.trim();
      }


      this.service.postEmail(formEncript).subscribe({
        next: (result: any) =>
          this.handlePostEmailSuccess(result, authMode, passwordReset, email),
        error: (error) => {
          console.error('login api error', error);
          this.spinner.set(false);
        },
      });
    } catch (error) {
      console.error('Encryption error:', error);
      this.spinner.set(false);
    }
  }

  /**
   * Validates password + captcha together so both error messages
   * can render simultaneously. Returns true if the form is valid.
   */
  private validateSignInForm(): boolean {
    this.loginSubmitted.set(true);

    let hasError = false;

    if (!this.loginForm.controls['pwd'].valid) {
      validationform.ValidateAllFormFields(this.loginForm);
      hasError = true;
    }

    if (this.captchaReady()) {
      this.captchaSubmitted = true;
      if (!this.captchaVerified) {
        hasError = true;
      }
    }

    if (hasError) {
      this.spinner.set(false);
      return false;
    }
    return true;
  }

  /**
   * Handles Flow 3 (second click): PASSWORD_OTP + signup_verified true,
   * OTP field already visible — this click verifies the OTP.
   * Returns true if this branch was handled (caller should return early).
   */
  private handleOtpOnlyClick(
    authMode: string,
    passwordReset: boolean,
    email: string,
  ): boolean {
    if (
      !(authMode === 'PASSWORD_OTP' && passwordReset && this.showOtpField())
    ) {
      return false;
    }

    if (!this.otpInputValue?.trim()) {
      return true; // block if OTP empty, but treat as handled
    }

    this.spinner.set(true);
    this.service.flowContext = { authMode, passwordReset, email };
    this.otpInput = this.otpInputValue;
    this.submitOtp();
    return true;
  }

  /** Encrypts the password using the existing Web Crypto (PBKDF2 + AES-GCM) logic. */
  private async encryptPassword(
    password: string,
  ): Promise<{ encryptedData: string; salt: string; passPhrase: string }> {
    const possible =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const passPhrase = this.makeRandom(16, possible);
    const salt = this.makeRandom(16, possible);
    const enc = new TextEncoder();

    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      enc.encode(passPhrase),
      { name: 'PBKDF2' },
      false,
      ['deriveKey'],
    );
    const aesKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: enc.encode(salt),
        iterations: 100_000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt'],
    );
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertextBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      aesKey,
      enc.encode(password),
    );
    const combined = new Uint8Array(
      iv.byteLength + ciphertextBuffer.byteLength,
    );
    combined.set(iv, 0);
    combined.set(new Uint8Array(ciphertextBuffer), iv.byteLength);
    const encryptedData = btoa(
      Array.from(combined)
        .map((b) => String.fromCharCode(b))
        .join(''),
    );

    return { encryptedData, salt, passPhrase };
  }

  /** Handles the postEmail() success callback for every auth-mode branch. */
  private handlePostEmailSuccess(
    result: any,
    authMode: string,
    passwordReset: boolean,
    email: string,
  ): void {
    this.verifyService.verifyToken = '';

    // Flow 3: PASSWORD_OTP + signup_verified true
    // Password correct → backend already sent OTP automatically.
    // Reveal the OTP input field; wait for user to enter OTP and click Sign In again.
    if (authMode === 'PASSWORD_OTP' && passwordReset) {
      this.showOtpField.set(true);
      this.notifier.notify(
        'default',
        'A verification code has been sent to your registered email and mobile.',
      );
      this.spinner.set(false);
      return;
    }

    // All other flows (Flow 4, PASSWORD_ONLY variants, legacy) log straight in.
    localStorage.setItem('loginuser', email);
    sessionStorage.setItem('isLoggedIn', 'true');
    this.navigateToDashboard(email);
    this.isText = false;
  }

  signUp() {
    this.formChange.emit('signup');
  }

  forgotpass() {
    this.formChange.emit('forgotpass');
  }

  generateCaptcha(): void {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    this.captchaText = Array.from(
      { length: 6 },
      () => chars[Math.floor(Math.random() * chars.length)],
    ).join('');
    this.captchaVerified = false;
    this.captchaInput = '';
    this.captchaSubmitted = false;

    // Use requestAnimationFrame — waits for Angular to finish rendering the DOM
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.drawCaptcha()); // double-rAF ensures DOM is painted
    });
  }

  drawCaptcha(): void {
    const canvas = document.getElementById(
      'captchaCanvas',
    ) as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;

    canvas.width = 180;
    canvas.height = 44;

    // Clean white background — matches the input field
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 180, 44);

    // Subtle noise lines
    for (let i = 0; i < 4; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random() * 180, Math.random() * 44);
      ctx.lineTo(Math.random() * 180, Math.random() * 44);
      ctx.strokeStyle = `hsla(${Math.random() * 360}, 30%, 75%, 0.5)`;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Letters — use system-ui to match Bootstrap/form font
    const colors = [
      '#1a56db',
      '#0f6e56',
      '#993c1d',
      '#534ab7',
      '#72243e',
      '#3b6d11',
    ];
    this.captchaText.split('').forEach((ch, i) => {
      ctx.save();
      const x = 18 + i * 26;
      const y = 28 + (Math.random() * 6 - 3);
      ctx.translate(x, y);
      ctx.rotate((Math.random() - 0.5) * 0.35); // subtle tilt, not extreme
      ctx.font = `600 20px system-ui, -apple-system, sans-serif`; // matches form font weight
      ctx.fillStyle = colors[i % colors.length];
      ctx.fillText(ch, 0, 0);
      ctx.restore();
    });

    // Subtle noise dots
    for (let i = 0; i < 25; i++) {
      ctx.beginPath();
      ctx.arc(Math.random() * 180, Math.random() * 44, 0.8, 0, 2 * Math.PI);
      ctx.fillStyle = 'rgba(0,0,0,0.06)';
      ctx.fill();
    }
  }

  verifyCaptcha(): void {
    this.captchaVerified = this.captchaInput.trim() === this.captchaText;
  }

  private navigateToDashboard(email: string): void {
    this.service.getUser().subscribe({
      next: (response: any) => {
        if (!response?.id || !response.is_active) {
          localStorage.clear();
          sessionStorage.clear();
          this.router.navigateByUrl('login');
          this.spinner.set(false);
          return;
        }
        sessionStorage.setItem(
          'clientId',
          response?.client_id?.[0]?.toString(),
        );
        sessionStorage.setItem('roleId', response?.role_id?.toString());

        this.service.userDetails = response;
        this.service.isAuthorizedUser = true;

        const userSalt = CryptoJS.lib.WordArray.random(16);
        const userIv = CryptoJS.lib.WordArray.random(16);
        const userKey = CryptoJS.PBKDF2(this.service.enCryptKey, userSalt, {
          keySize: 32,
          iterations: 128,
          hasher: CryptoJS.algo.SHA256,
        });
        const encryptedUser = CryptoJS.AES.encrypt(
          JSON.stringify(response),
          userKey,
          { iv: userIv },
        );
        const storedUser =
          userSalt.toString() +
          '::' +
          userIv.toString() +
          '::' +
          encryptedUser.toString();

        localStorage.setItem('loginuser', email);
        localStorage.setItem('user', storedUser);

        const hasRfp = this.checkRfpExists(response);
        if (hasRfp) {
          this.router.navigateByUrl('main/rfpcontracts');
          localStorage.setItem('selectedApps', 'rfpcontract');
        } else {
          this.router.navigateByUrl('dashboard');
          this.notifier.notify(
            'default',
            'RFP access is not enabled for this account. Please contact the administrator to enable the required access.',
          );
          this.spinner.set(false);
          return;
        }

        this.mainService.selectedAppsBS.next({
          text: 'Document Analytics and Process Automation',
          icon: 'bi bi-file-earmark',
          url: '/main/rfpcontracts',
          name: 'rfpcontract',
        });
        this.spinner.set(false);
      },
      error: (err: HttpErrorResponse) => {
        this.spinner.set(false);
      },
    });
  }

  onOtpInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Strip everything except digits, and remove spaces
    input.value = input.value.replace(/[^0-9]/g, '');
    this.otpInputValue = input.value;
  }

  removePasswordSpaces(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\s/g, '');

    if (input.value !== value) {
      input.value = value;
      this.loginForm.get('pwd')?.setValue(value);
    }
  }
}
